import React, { useState } from "react";
import { db } from "./firebase";
import { doc, setDoc, collection, query, where, getDocs } from "firebase/firestore";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import md5 from 'md5';
import { getHashedIP, trackActivity, detectSuspiciousActivity } from './utils';
import { moderateUsername } from './utils/contentModeration';
import { isNative } from './utils/isNative';
import { signInWithGoogle } from './utils/googleAuth';
import { signInWithApple } from './utils/appleAuth';
import { createUserWithEmail, updateUserProfile, signOutUser } from './utils/authHelpers';

export default function Signup() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const getFriendlyErrorMessage = (errorCode) => {
    switch (errorCode) {
      case 'auth/email-already-in-use':
        return t('auth.errors.emailInUse');
      case 'auth/invalid-email':
        return t('auth.errors.invalidEmail');
      case 'auth/weak-password':
        return t('auth.errors.weakPassword');
      case 'auth/user-not-found':
        return t('auth.errors.userNotFound');
      case 'auth/wrong-password':
        return t('auth.errors.wrongPassword');
      default:
        return t('auth.errors.general');
    }
  };

  const getClientIP = async () => {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip;
    } catch (error) {
      console.warn("Errore nel recupero IP:", error);
      return null;
    }
  };

  const handleEmailSignup = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    if (!username.trim()) {
      setError(t('errors.nameRequired'));
      setLoading(false);
      return;
    }

    if (username.trim().length < 2) {
      setError(t('auth.errors.usernameTooShort'));
      setLoading(false);
      return;
    }

    // 🛡️ Username moderation check
    try {
      console.log("🛡️ Moderazione username:", username.trim());
      const moderationResult = await moderateUsername(username.trim());
      
      if (!moderationResult.allowed) {
        console.log("🚫 Username bloccato:", moderationResult.reason);
        setError(t('auth.errors.inappropriateUsername'));
        setLoading(false);
        return;
      }
      
      console.log("✅ Username approvato dalla moderazione");
    } catch (moderationError) {
      console.error("❌ Errore moderazione username:", moderationError);
      setError(t('auth.errors.usernameCheckFailed'));
      setLoading(false);
      return;
    }

    try {
      // Ottieni IP e crea hash
      const clientIP = await getClientIP();
      const ipHash = clientIP ? md5(clientIP) : null;

      // Verifica limite IP usando Cloud Function - con fallback
      if (ipHash) {
        try {
          console.log("🔍 Inizio verifica IP:", ipHash);
          const { httpsCallable } = await import('firebase/functions');
          const { getFunctions } = await import('firebase/functions');
          const functions = getFunctions();
          const checkLimit = httpsCallable(functions, 'checkRegistrationLimit');
          
          console.log("📞 Chiamo checkRegistrationLimit...");
          const result = await checkLimit({ ipHash });
          console.log("📞 Risposta checkRegistrationLimit:", result.data);
          
          if (!result.data.allowed) {
            console.log("🔒 Limite IP raggiunto:", result.data.message);
            setError(t('auth.errors.ipLimitReached'));
            setLoading(false);
            return;
          }
          
          console.log("✅ Verifica IP completata, procedo con la registrazione");
        } catch (ipError) {
          console.error("❌ ERRORE verifica IP:", ipError);
          console.error("❌ Error code:", ipError.code);
          console.error("❌ Error message:", ipError.message);
          console.warn("⚠️ Procedo comunque con la registrazione");
          // Procedi con la registrazione anche se il controllo IP fallisce
        }
      } else {
        console.warn("⚠️ IP non disponibile, procedo senza controllo IP");
      }

      // Crea l'account Firebase
      const userCredential = await createUserWithEmail(email, password);
      const user = userCredential.user;

      // Salva i dati utente
      try {
        await setDoc(doc(db, 'users', user.uid), {
          uid: user.uid,
          displayName: username.trim(),
          email: user.email || email,
          avatar: '',
          following: [],
          followers: [],
          language: 'it',
          isPremium: false,
          isNewUser: true,
          ipHash: ipHash || null,
          createdAt: new Date(),
          updatedAt: new Date()
        });

        // Update Firebase Auth profile with the username
        await updateUserProfile({
          displayName: username.trim()
        });

        // Registra l'IP dopo la creazione dell'account - OPZIONALE
        if (ipHash) {
          try {
            console.log("📝 Inizio registrazione IP:", ipHash, "per user:", user.uid);
            const { httpsCallable } = await import('firebase/functions');
            const { getFunctions } = await import('firebase/functions');
            const functions = getFunctions();
            const recordIP = httpsCallable(functions, 'recordRegistration');
            
            const recordResult = await recordIP({ ipHash, userId: user.uid });
            console.log("📝 Risposta recordRegistration:", recordResult.data);
            console.log("✅ IP registrato correttamente");
          } catch (ipRecordError) {
            console.error("❌ ERRORE registrazione IP:", ipRecordError);
            console.error("❌ Error code:", ipRecordError.code);
            console.error("❌ Error message:", ipRecordError.message);
            console.warn("⚠️ Account creato correttamente, ma errore nella registrazione IP");
            // Non eliminiamo l'account - l'utente può comunque usare l'app
          }
        } else {
          console.log("🔍 IP non disponibile, salto registrazione IP");
        }

        navigate('/');
      } catch (dbError) {
        console.error("❌ Errore salvataggio utente:", dbError);
        setError(t('auth.errors.profileSaveError'));
      }
    } catch (err) {
      console.error("Errore registrazione:", err);
      const friendly = getFriendlyErrorMessage(err.code);
      setError(friendly);
    }

    setLoading(false);
  };

  const handleOAuthSignup = async (provider, providerName) => {
    setError("");
    setLoading(true);

    try {
      const result = await provider();
      const user = result.user;

      // Ottieni IP
      const clientIP = await getClientIP();
      const ipHash = clientIP ? md5(clientIP) : null;

      // Verifica se l'utente esiste già
      const userDoc = await getDocs(query(collection(db, 'users'), where('uid', '==', user.uid)));

      if (userDoc.empty) {
        // Nuovo utente - APPLICA CONTROLLI IP se disponibile
        if (ipHash) {
          try {
            console.log(`🔍 Verifica IP per ${providerName} signup:`, ipHash);
            const { httpsCallable } = await import('firebase/functions');
            const { getFunctions } = await import('firebase/functions');
            const functions = getFunctions();
            const checkLimit = httpsCallable(functions, 'checkRegistrationLimit');
            
            console.log(`📞 Chiamo checkRegistrationLimit per ${providerName}...`);
            const result = await checkLimit({ ipHash });
            console.log(`📞 Risposta ${providerName} checkRegistrationLimit:`, result.data);
            
            if (!result.data.allowed) {
              console.log(`🔒 Limite IP raggiunto per ${providerName} signup`);
              await signOutUser();
              setError(t('auth.errors.ipLimitReached'));
              setLoading(false);
              return;
            }
          } catch (ipError) {
            console.error(`❌ ERRORE verifica IP per ${providerName}:`, ipError);
            console.error("❌ Error code:", ipError.code);
            console.error("❌ Error message:", ipError.message);
            console.warn(`⚠️ Procedo comunque con ${providerName} signup`);
          }
        } else {
          console.warn(`⚠️ IP non disponibile per ${providerName} signup, procedo senza controllo`);
        }

        await setDoc(doc(db, 'users', user.uid), {
          uid: user.uid,
          displayName: user.displayName || (user.email ? user.email.split('@')[0] : "User"),
          email: user.email || null,
          avatar: '',
          following: [],
          followers: [],
          language: 'it',
          isPremium: false,
          isNewUser: true,
          ipHash: ipHash || null,
          createdAt: new Date(),
          updatedAt: new Date()
        });

        // Registra IP - OPZIONALE  
        if (ipHash) {
          try {
            console.log(`📝 Registrazione IP per ${providerName} signup:`, ipHash, "user:", user.uid);
            const { httpsCallable } = await import('firebase/functions');
            const { getFunctions } = await import('firebase/functions');
            const functions = getFunctions();
            const recordIP = httpsCallable(functions, 'recordRegistration');
            
            const recordResult = await recordIP({ ipHash, userId: user.uid });
            console.log(`📝 Risposta ${providerName} recordRegistration:`, recordResult.data);
            console.log(`✅ IP registrato correttamente per ${providerName} signup`);
          } catch (ipRecordError) {
            console.error(`❌ ERRORE registrazione IP per ${providerName}:`, ipRecordError);
            console.error("❌ Error code:", ipRecordError.code);
            console.error("❌ Error message:", ipRecordError.message);
            console.warn(`⚠️ Account ${providerName} creato correttamente, ma errore registrazione IP`);
          }
        } else {
          console.log(`🔍 IP non disponibile per ${providerName} signup, salto registrazione IP`);
        }
      }

      navigate('/');
    } catch (err) {
      console.error(`Errore ${providerName} signup:`, err);
      const errorMsg = providerName === 'Google' 
        ? t('auth.errors.googleSignupError')
        : t('auth.errors.appleSignupError');
      setError(errorMsg);
    }

    setLoading(false);
  };

  const handleGoogleSignup = async () => {
    await handleOAuthSignup(signInWithGoogle, "Google");
  };

  const handleAppleSignup = async () => {
    await handleOAuthSignup(signInWithApple, "Apple");
  };

  return (
    <div style={styles.container}>
      {/* Hero Section */}
      <div style={styles.hero}>
        <h1 style={styles.heroTitle}>
          {t('hero.welcomeTitle')}
        </h1>
        <p style={styles.heroSubtitle}>
          {t('hero.welcomeSubtitle')}
        </p>
      </div>
      
      <div style={styles.form}>
        <form onSubmit={handleEmailSignup}>
          <input
            type="text"
            placeholder={t('auth.username')}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            style={styles.input}
            required
          />
          <input
            type="email"
            placeholder={t('auth.email')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={styles.input}
            required
          />
          <div style={styles.passwordContainer}>
            <input
              type={showPassword ? "text" : "password"}
              placeholder={t('auth.password')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={styles.passwordInput}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              style={styles.togglePassword}
            >
              {showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
            </button>
          </div>
          {error && <div style={styles.error}>{error}</div>}
          <button type="submit" disabled={loading} style={styles.button}>
            {loading ? t('common.loading') : t('auth.signup.createAccount')}
          </button>
        </form>

        <button onClick={handleGoogleSignup} disabled={loading} style={styles.googleButton}>
          <svg width="18" height="18" viewBox="0 0 24 24" style={styles.googleIcon}>
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          {t('auth.continueWithGoogle')}
        </button>

        <button onClick={handleAppleSignup} disabled={loading} style={styles.appleButton}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 170 170"
            style={{ marginRight: 10 }}
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M150.37 130.25c-2.45 5.66-5.35 10.87-8.71 15.66-4.58 6.53-8.33 11.05-11.22 13.56-4.48 4.12-9.28 6.23-14.42 6.35-3.69 0-8.14-1.05-13.32-3.18-5.197-2.12-9.973-3.17-14.34-3.17-4.58 0-9.492 1.05-14.746 3.17-5.262 2.13-9.501 3.24-12.742 3.35-4.929 0.21-9.842-1.96-14.746-6.52-3.13-2.73-7.045-7.41-11.735-14.04-5.032-7.08-9.169-15.29-12.41-24.65-3.471-10.11-5.211-19.9-5.211-29.378 0-10.857 2.346-20.221 7.045-28.068 3.693-6.303 8.606-11.275 14.755-14.925s12.793-5.51 19.948-5.629c3.915 0 9.049 1.211 15.429 3.591 6.362 2.388 10.447 3.599 12.238 3.599 1.339 0 5.877-1.416 13.57-4.239 7.275-2.618 13.415-3.702 18.445-3.275 13.63 1.1 23.87 6.473 30.68 16.153-12.19 7.386-18.22 17.731-18.1 31.002 0.11 10.337 3.86 18.939 11.23 25.769 3.34 3.17 7.07 5.62 11.22 7.36-0.9 2.61-1.85 5.11-2.86 7.51zM119.11 7.24c0 8.102-2.96 15.667-8.86 22.669-7.12 8.324-15.732 13.134-25.071 12.375a25.222 25.222 0 0 1-0.188-3.07c0-7.778 3.386-16.102 9.399-22.908 3.002-3.446 6.82-6.311 11.45-8.597 4.62-2.252 8.99-3.497 13.1-3.71 0.12 1.083 0.17 2.166 0.17 3.24z"/>
          </svg>
          {t('auth.continueWithApple')}
        </button>

        <div style={styles.toggleText}>
          {t('auth.signup.alreadyHaveAccount')}{' '}
          <Link to="/login" style={styles.link}>{t('auth.signup.loginLink')}</Link>
        </div>

        <div style={styles.footerLinks}>
          <Link to="/privacy" style={styles.footerLink}>{t('legal.privacyPolicy')}</Link>
          <Link to="/terms" style={styles.footerLink}>{t('legal.termsAndConditions')}</Link>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    backgroundColor: "#fff",
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    fontFamily: "Poppins, sans-serif",
    padding: "16px",
    margin: "0 auto",
    maxWidth: "400px",
  },
  hero: {
    textAlign: "center",
    marginBottom: "24px",
    maxWidth: "100%",
    width: "100%",
  },
  heroTitle: {
    fontSize: "28px",
    fontWeight: "700",
    color: "#000",
    marginBottom: "6px",
    lineHeight: "1.3",
    fontFamily: "Poppins, sans-serif",
    textAlign: "center",
  },
  heroSubtitle: {
    fontSize: "14px",
    color: "#555",
    marginBottom: "0",
    lineHeight: "1.5",
    margin: 0,
    fontWeight: "400",
    fontFamily: "Poppins, sans-serif",
    textAlign: "center",
  },
  form: {
    backgroundColor: "#f9f9f9",
    padding: "24px",
    borderRadius: "12px",
    boxShadow: "0px 2px 8px rgba(0,0,0,0.05)",
    width: "100%",
    textAlign: "center"
  },
  input: {
    padding: "12px",
    fontSize: "14px",
    borderRadius: "6px",
    border: "1px solid #ccc",
    width: "100%",
    boxSizing: "border-box",
    marginBottom: "12px",
    fontFamily: "Poppins, sans-serif"
  },
  passwordContainer: {
    position: "relative",
    marginBottom: "12px"
  },
  passwordInput: {
    padding: "12px",
    paddingRight: "70px",
    fontSize: "14px",
    borderRadius: "6px",
    border: "1px solid #ccc",
    width: "100%",
    boxSizing: "border-box",
    fontFamily: "Poppins, sans-serif"
  },
  togglePassword: {
    position: "absolute",
    right: "10px",
    top: "50%",
    transform: "translateY(-50%)",
    fontSize: "12px",
    color: "#333",
    cursor: "pointer",
    background: "none",
    border: "none"
  },
  button: {
    padding: "12px",
    backgroundColor: "#111",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    fontWeight: "bold",
    cursor: "pointer",
    width: "100%",
    fontSize: "14px",
    marginBottom: "8px"
  },
  googleButton: {
    padding: "12px",
    backgroundColor: "#fff",
    color: "#444",
    border: "1px solid #ccc",
    borderRadius: "6px",
    fontWeight: "500",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
    width: "100%",
    fontSize: "14px",
    marginBottom: "8px"
  },
  googleIcon: {
    marginRight: "8px"
  },
  appleButton: {
    padding: "12px",
    backgroundColor: "#fff",
    color: "#000",
    border: "1px solid #ccc",
    borderRadius: "6px",
    fontWeight: "500",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
    width: "100%",
    fontSize: "14px",
    marginBottom: "16px"
  },
  appleIcon: {
    marginRight: "8px"
  },
  toggleText: {
    marginTop: "20px",
    fontSize: "16px",
    textAlign: "center",
    color: "#666",
    fontWeight: "500"
  },
  link: {
    color: "#111",
    cursor: "pointer",
    fontWeight: "bold",
    textDecoration: "none"
  },
  footerLinks: {
    display: "flex",
    justifyContent: "center",
    gap: "16px",
    marginTop: "20px",
    fontSize: "12px"
  },
  footerLink: {
    color: "#111",
    cursor: "pointer",
    textDecoration: "underline"
  },
  error: {
    marginTop: "8px",
    fontSize: "13px",
    color: "red",
    marginBottom: "15px"
  }
};